import { useState, type FormEvent } from 'react';
import { useAuthStore } from '@/store/authStore';
import Card, { CardHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Alert from '@/components/ui/Alert';
import { settingsApi } from '@/services/settings';
import { ApiClientError } from '@/services/api';

/**
 * Profile settings component
 * Allows users to view and update their profile information
 */
export default function ProfileSettings() {
  const { userName, userEmail, setUser } = useAuthStore();
  const [name, setName] = useState(userName || '');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const updatedUser = await settingsApi.updateProfile({ name: name.trim() });
      setUser(updatedUser);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else {
        setError('Failed to update profile. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const hasChanges = name.trim() !== userName;

  return (
    <Card>
      <CardHeader
        title="Profile"
        description="Update your personal information"
      />

      {success && (
        <Alert variant="success" className="mb-4">
          Profile updated successfully.
        </Alert>
      )}

      {error && (
        <Alert variant="error" className="mb-4" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Display name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Alex, FamilyAdmin"
          required
          minLength={2}
          maxLength={100}
          hint="For privacy, consider using a nickname instead of your real name"
        />

        <Input
          label="Email address"
          type="email"
          value={userEmail || ''}
          disabled
          hint="Contact support to change your email address"
        />

        <div className="pt-4">
          <Button
            type="submit"
            disabled={!hasChanges || !name.trim()}
            isLoading={isLoading}
          >
            Save changes
          </Button>
        </div>
      </form>
    </Card>
  );
}
